import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface ModalProps {
  abierto: boolean
  titulo: string
  children: ReactNode
  pie?: ReactNode
  ancho?: number
  onCerrar: () => void
  /** Fondo y color del contenedor (anula bg-superficie/text-texto si se usa). */
  style?: React.CSSProperties
  /** Texto blanco para fondos oscuros. */
  claro?: boolean
  /** Centra el modal vertical y horizontalmente. */
  centrado?: boolean
}

export function Modal({ abierto, titulo, children, pie, ancho, onCerrar, style, claro, centrado = true }: ModalProps) {
  const [visible, setVisible] = useState(abierto)
  const [cerrando, setCerrando] = useState(false)

  useEffect(() => {
    if (abierto) {
      setVisible(true)
      setCerrando(false)
      return
    }
    if (!visible) return
    setCerrando(true)
    const temporizador = window.setTimeout(() => {
      setVisible(false)
      setCerrando(false)
    }, 300)
    return () => window.clearTimeout(temporizador)
  }, [abierto, visible])

  useEffect(() => {
    if (!visible) return
    const alPresionar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
    }
    window.addEventListener('keydown', alPresionar)
    return () => window.removeEventListener('keydown', alPresionar)
  }, [visible, onCerrar])

  if (!visible) return null

  // Un backdrop-filter en el overlay lo vuelve "backdrop root": el del panel
  // hijo ya no veria nada detras. Por eso, cuando quien llama pide blur en el
  // panel, el desenfoque del fondo se pinta en una capa hermana (no ancestro)
  // del panel, que si puede convivir con el suyo.
  const blurPropio = !!(style?.backdropFilter || (style as Record<string, unknown>)?.WebkitBackdropFilter)

  // Portal a <body>: dentro de <main class="vista anima-entrada"> la animacion
  // de opacity crea un backdrop root y el backdrop-filter del panel no aplica.
  return createPortal(
    <div
      className={`fixed inset-0 z-60 flex ${centrado ? 'items-center p-4' : 'items-start p-0'} justify-center ${blurPropio ? '' : 'backdrop-blur-sm'}`}
      onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}
    >
      {blurPropio && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(1px)' }}
          aria-hidden
        />
      )}
      <div
        className={`relative ${centrado ? 'my-0' : 'mt-8 mb-2'} max-h-[calc(100vh-2rem)] w-full  overflow-auto rounded-2xl border border-borde p-2 shadow-2xl bg-superficie ${claro ? 'text-white' : 'text-texto'} ${cerrando ? 'anima-disminuir' : 'anima-crecer'}`}
        style={{
          maxWidth: ancho ? `${ancho}px` : '768px',
          ...style,
          ...(style?.backdropFilter ? { WebkitBackdropFilter: style.backdropFilter } : {}),
        }}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className={`m-0 text-xl font-bold ${claro ? '!text-white' : 'text-texto'}`}>{titulo}</h2>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className={`flex h-8 w-8 items-center justify-center rounded-full bg-black/10 dark:bg-gray-800/70 transition ${claro ? '!text-white/80 hover:!text-white' : 'text-texto-suave hover:text-texto'}`}
          >
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[calc(100vh-13rem)] overflow-y-auto pr-1">{children}</div>
        {pie && <div className="mt-4 flex justify-end gap-2.5 border-t border-borde pt-4">{pie}</div>}
      </div>
    </div>,
    document.body
  )
}
